import axios from 'axios';
import { GET_PRESIGNED_UPLOAD_URL } from '../api/preSignedUrl';

/**
 * Upload a single file to AWS S3 using pre-signed URL
 * @param {File} file - The file object from input[type="file"]
 * @param {string} folder - Folder prefix in S3 (e.g. 'capitaloffice')
 * @returns {Promise<{name: string, url: string, size: string, docType: string}>}
 */
export const uploadFileToS3 = async (file, folder = 'capitaloffice', docType = 'Supporting Document') => {
    if (!file) throw new Error('No file provided for upload');

    const cleanName = (file.name || 'document').replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${folder}/${Date.now()}_${cleanName}`;
    const contentType = file.type || 'application/octet-stream';

    // 1. Get Pre-signed URL from backend
    const { data: s3Data } = await axios.post(GET_PRESIGNED_UPLOAD_URL, {
        path,
        contentType,
    });

    if (!s3Data || !s3Data.uploadUrl) {
        throw new Error('Failed to generate pre-signed upload URL from server');
    }

    // 2. Direct S3 Upload
    await axios.put(s3Data.uploadUrl, file, {
        headers: { 'Content-Type': contentType },
    });

    // 3. Return document object with public S3 URL
    return {
        name: file.name,
        url: s3Data.publicUrl,
        size: (file.size / 1024).toFixed(1) + ' KB',
        docType: file.docType || docType,
    };
};

/**
 * Upload multiple files to AWS S3
 * Handles both already-uploaded document objects (with url) and pending File objects.
 * @param {Array<File|Object>} documentsList - Array of files or existing doc objects
 * @param {string} folder - Folder prefix in S3
 * @returns {Promise<Array<{name: string, url: string, size: string, docType: string}>>}
 */
export const uploadDocumentsToS3 = async (documentsList = [], folder = 'capitaloffice') => {
    const uploadedDocs = [];

    for (const doc of documentsList) {
        // If it's already uploaded (has a URL and no raw file object attached)
        if (doc && doc.url && !doc.rawFile) {
            uploadedDocs.push(doc);
            continue;
        }

        // If it has a raw File object that needs to be uploaded to S3
        const fileToUpload = doc.rawFile || (doc instanceof File ? doc : null);
        if (fileToUpload) {
            const uploaded = await uploadFileToS3(fileToUpload, folder, doc.docType || 'Supporting Document');
            uploadedDocs.push(uploaded);
        }
    }

    return uploadedDocs;
};
